import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  GraduationCap,
  TrendingUp,
  Award,
  BookOpen,
  Calendar,
  BarChart3,
  FileText,
  CheckCircle,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";
import { getStudentGradesForCourse } from "../../services/exams";

const StudentGrades: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;

  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-grades"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  // Fetch exam grades for current courses
  const { data: examGradesData, isLoading: examGradesLoading } = useQuery({
    queryKey: ["student-exam-grades", user?.id],
    queryFn: async () => {
      if (!studentData?.data?.courses || studentData.data.courses.length === 0)
        return null;

      const examGradesPromises = studentData.data.courses.map(
        async (course: any) => {
          try {
            const courseId = course._id || course;
            const result = await getStudentGradesForCourse(courseId);
            return {
              course:
                result.course ||
                (typeof course === "string"
                  ? { _id: course, name: "Unknown Course" }
                  : course),
              examData: result,
              error: null,
            };
          } catch (error) {
            console.error(
              `Error fetching grades for course ${course._id || course}:`,
              error
            );
            return {
              course:
                typeof course === "string"
                  ? { _id: course, name: "Unknown Course" }
                  : course,
              examData: { grades: [], overallGrade: null },
              error: error.message,
            };
          }
        }
      );

      const results = await Promise.all(examGradesPromises);
      return results;
    },
    enabled:
      !!user?.id &&
      !!studentData?.data?.courses &&
      studentData.data.courses.length > 0,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Award className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-red-900 font-semibold text-lg">
                Error Loading Grades
              </h3>
              <p className="text-red-700 mt-2">
                Unable to load your grades. Please try refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Grade management is not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Calculate grade statistics
  const calculateGradeStats = () => {
    let allGrades: number[] = [];
    let gradeDistribution: { [key: string]: number } = {};

    // Collect grades from academic records
    if (student?.academicRecords && student.academicRecords.length > 0) {
      student.academicRecords.forEach((record: any) => {
        if (record.grades) {
          Object.values(record.grades).forEach((grade: any) => {
            const numGrade = Number(grade);
            if (!isNaN(numGrade)) {
              allGrades.push(numGrade);
              const gradeRange = getGradeRange(numGrade);
              gradeDistribution[gradeRange] =
                (gradeDistribution[gradeRange] || 0) + 1;
            }
          });
        }
      });
    }

    // Collect grades from exam data
    if (examGradesData && examGradesData.length > 0) {
      examGradesData.forEach((courseData: any) => {
        if (
          courseData.examData?.grades &&
          courseData.examData.grades.length > 0
        ) {
          courseData.examData.grades.forEach((grade: any) => {
            if (grade.percentage && !isNaN(grade.percentage)) {
              allGrades.push(grade.percentage);
              const gradeRange = getGradeRange(grade.percentage);
              gradeDistribution[gradeRange] =
                (gradeDistribution[gradeRange] || 0) + 1;
            }
          });
        }
      });
    }

    return {
      totalSubjects: allGrades.length,
      averageGrade:
        allGrades.length > 0
          ? allGrades.reduce((a, b) => a + b, 0) / allGrades.length
          : 0,
      highestGrade: allGrades.length > 0 ? Math.max(...allGrades) : 0,
      lowestGrade: allGrades.length > 0 ? Math.min(...allGrades) : 0,
      gradeDistribution,
    };
  };

  const getGradeRange = (grade: number): string => {
    // Use hardcoded grade boundaries
    if (grade >= 70) return "A";
    if (grade >= 60) return "B";
    if (grade >= 50) return "C";
    if (grade >= 40) return "D";
    return "F";
  };

  const getGradeColor = (grade: number): string => {
    const gradeLetter = getGradeRange(grade);

    // Use color based on grade letter
    switch (gradeLetter) {
      case "A":
        return "text-green-600";
      case "B":
        return "text-blue-600";
      case "C":
        return "text-yellow-600";
      case "D":
        return "text-orange-600";
      case "F":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getGradeBadgeStyle = (grade: string): string => {
    switch (grade) {
      case "A":
        return "bg-gradient-to-r from-green-500 to-green-600 text-white";
      case "B":
        return "bg-gradient-to-r from-blue-500 to-blue-600 text-white";
      case "C":
        return "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white";
      case "D":
        return "bg-gradient-to-r from-orange-500 to-orange-600 text-white";
      case "F":
        return "bg-gradient-to-r from-red-500 to-red-600 text-white";
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-600 text-white";
    }
  };

  const stats = calculateGradeStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 px-0 py-2">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
          My Grades
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-lg font-medium">
          Track your academic performance across all subjects
        </p>
      </div>

      {/* Grade Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Subjects */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Total Subjects
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats.totalSubjects}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <BookOpen className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Average Grade */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Average Grade
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {Math.trunc(stats.averageGrade)}%
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <BarChart3 className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Highest Grade */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">
                Highest Grade
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {Math.trunc(stats.highestGrade)}%
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg">
              <Award className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Grade Range */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Grade Range</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {getGradeRange(stats.averageGrade)}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <TrendingUp className="h-7 w-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Grade Distribution */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Grade Distribution
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-md">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
          </div>

          {Object.keys(stats.gradeDistribution).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(stats.gradeDistribution).map(([grade, count]) => (
                <div
                  key={grade}
                  className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-50 to-cyan-50 rounded-xl border border-slate-200/50 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                        grade === "A"
                          ? "bg-gradient-to-br from-green-500 to-emerald-500"
                          : grade === "B"
                          ? "bg-gradient-to-br from-blue-500 to-indigo-500"
                          : grade === "C"
                          ? "bg-gradient-to-br from-yellow-500 to-orange-500"
                          : grade === "D"
                          ? "bg-gradient-to-br from-orange-500 to-red-500"
                          : "bg-gradient-to-br from-red-500 to-pink-500"
                      }`}
                    >
                      <span className="text-white font-bold text-lg">
                        {grade}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">
                      Grade {grade}
                    </span>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-gradient-to-r from-slate-200 to-slate-300 shadow-sm">
                    <span className="text-sm font-bold text-slate-900">
                      {count} {count === 1 ? "subject" : "subjects"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                <BarChart3 className="h-8 w-8 text-slate-500" />
              </div>
              <h3 className="text-base font-semibold text-slate-900">
                No grades available
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Grades will appear here once they are posted.
              </p>
            </div>
          )}
        </div>

        {/* Academic Records */}
        <div className="lg:col-span-2">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                Academic Records
              </h2>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
                <GraduationCap className="h-5 w-5 text-white" />
              </div>
            </div>

            {student?.academicRecords && student.academicRecords.length > 0 ? (
              <div className="space-y-6">
                {student.academicRecords.map((record: any, index: number) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-slate-50 to-emerald-50 border border-slate-200/60 rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
                          <Calendar className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="font-bold text-slate-900 text-lg">
                          {record.term || `Academic Term ${index + 1}`}
                        </h3>
                      </div>
                      <span className="text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 px-3 py-1 rounded-full shadow-md">
                        Class: {record.classId || "N/A"}
                      </span>
                    </div>

                    {record.grades && Object.keys(record.grades).length > 0 ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(record.grades).map(
                            ([subject, grade]) => {
                              const numGrade = Number(grade);
                              return (
                                <div
                                  key={subject}
                                  className="flex items-center justify-between p-4 bg-white/90 border border-slate-200/60 rounded-xl hover:shadow-md transition-all duration-300 hover:scale-[1.02]"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                                      <BookOpen className="h-5 w-5 text-white" />
                                    </div>
                                    <span className="font-semibold text-slate-900">
                                      {subject}
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <span
                                      className={`text-xl font-bold ${getGradeColor(
                                        numGrade
                                      )}`}
                                    >
                                      {Math.trunc(numGrade)}
                                    </span>
                                    <div className="flex items-center justify-end space-x-2 mt-1">
                                      <span
                                        className={`px-3 py-1 rounded-full text-xs font-bold shadow-md ${getGradeBadgeStyle(
                                          getGradeRange(numGrade)
                                        )}`}
                                      >
                                        {getGradeRange(numGrade)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>

                        {/* Term Summary */}
                        <div className="mt-4 pt-4 border-t border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-700">
                              Term Average:
                            </span>
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-slate-900 text-lg">
                                {Object.values(record.grades).length > 0
                                  ? Math.trunc(
                                      (Object.values(record.grades).reduce(
                                        (sum: number, grade: any) =>
                                          sum + Number(grade),
                                        0
                                      ) as number) /
                                        Object.values(record.grades).length
                                    )
                                  : "N/A"}
                              </span>
                              {Object.values(record.grades).length > 0 && (
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-bold shadow-md ${getGradeBadgeStyle(
                                    getGradeRange(
                                      (Object.values(record.grades).reduce(
                                        (sum: number, grade: any) =>
                                          sum + Number(grade),
                                        0
                                      ) as number) /
                                        Object.values(record.grades).length
                                    )
                                  )}`}
                                >
                                  {getGradeRange(
                                    (Object.values(record.grades).reduce(
                                      (sum: number, grade: any) =>
                                        sum + Number(grade),
                                      0
                                    ) as number) /
                                      Object.values(record.grades).length
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                          <BookOpen className="h-7 w-7 text-slate-500" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900">
                          No grades recorded for this term
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                  <GraduationCap className="h-10 w-10 text-slate-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  No academic records found
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  Your grades and academic records will appear here once they
                  are posted by your teachers.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exam Grades Section */}
      <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 py-4 px-2">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Exam Grades</h2>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md">
            <FileText className="h-6 w-6 text-white" />
          </div>
        </div>

        {examGradesLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : examGradesData && examGradesData.length > 0 ? (
          <div className="space-y-6">
            {examGradesData.map((courseData: any, index: number) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-50 to-indigo-50 border border-slate-200/60 rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md">
                      <BookOpen className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {courseData.course.name}
                    </h3>
                  </div>
                  {courseData.examData.overallGrade && (
                    <div className="text-right">
                      <div className="text-3xl font-bold text-slate-900 mb-1">
                        {courseData.examData.overallGrade.overallGrade}
                      </div>
                      <div className="text-sm font-semibold text-slate-600 mb-2">
                        {Math.trunc(
                          courseData.examData.overallGrade.overallPercentage
                        )}
                        %
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-bold shadow-md ${getGradeBadgeStyle(
                          courseData.examData.overallGrade.overallGrade
                        )}`}
                      >
                        {courseData.examData.overallGrade.overallGrade}
                      </span>
                    </div>
                  )}
                </div>

                {courseData.examData.grades &&
                courseData.examData.grades.length > 0 ? (
                  <div className="space-y-3">
                    {courseData.examData.grades.map((grade: any) => (
                      <div
                        key={grade._id}
                        className="flex items-center justify-between py-4 px-4 bg-white/90 border border-slate-200/60 rounded-xl hover:shadow-md transition-all duration-300 hover:scale-[1.01]"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
                            <FileText className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">
                              {grade.examId.title}
                            </div>
                            <div className="text-sm text-slate-600 font-medium">
                              {new Date(
                                grade.examId.schedule.date
                              ).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm sm:text-xl font-bold text-slate-900">
                            {grade.marks}/{grade.maxMarks}
                          </div>
                          <div className="flex items-center justify-end space-x-2 mt-1">
                            <span className="text-sm text-slate-700 font-semibold">
                              {Math.trunc(grade.percentage)}%
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-bold shadow-md ${getGradeBadgeStyle(
                                grade.grade
                              )}`}
                            >
                              {grade.grade}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
                      <CheckCircle className="h-7 w-7 text-slate-500" />
                    </div>
                    <p className="text-sm font-semibold text-slate-900">
                      {courseData.error
                        ? `Error loading grades: ${courseData.error}`
                        : "No exam grades available for this course yet"}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mb-4">
              <FileText className="h-10 w-10 text-slate-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              No exam grades available
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Your exam grades will appear here once they are posted by your
              teachers.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentGrades;
